import * as yup from 'yup'

import { getFirstAndSecondName } from '@/utils/getFirstAndSecondName'

export const getInitialsChar = (
  name: string,
  quantity: number = 2,
  toUpperCase: boolean = true
) => {
  const str = name.substring(0, quantity)
  return toUpperCase ? str.toUpperCase() : str
}

export function capitalizeFirstLetter(str: string): string {
  if (!str) {
    return ''
  }
  const lowercased = str.toLowerCase()
  return lowercased.charAt(0).toUpperCase() + lowercased.slice(1)
}

export const getQueryString = (data: Record<string, any>) => {
  const query = new URLSearchParams()
  Object.entries(data).forEach(([key, value]) => {
    if (value) {
      query.append(key, value.toString())
    }
  })

  return query.toString()
}

const calcCPFDigit = (cpf: string, length: number): number => {
  const sum = Array.from({ length }, (_, i) => parseInt(cpf[i]) * (length + 1 - i)).reduce(
    (acc, val) => acc + val,
    0
  )
  const remainder = (sum * 10) % 11
  return remainder >= 10 ? 0 : remainder
}

export const validateCPF = (cpf: string): boolean => {
  const digits = cpf.replace(/[^\d]+/g, '')
  if (digits.length !== 11 || /^(\d)\1+$/.test(digits)) return false
  return (
    calcCPFDigit(digits, 9) === parseInt(digits[9]) &&
    calcCPFDigit(digits, 10) === parseInt(digits[10])
  )
}

export const validateDate = (data) => {
  try {
    new Date(data)
    return true
  } catch (error) {
    return false
  }
}

const emailSchema = yup.object().shape({
  email: yup.string().email('Email inválido').required('Email é obrigatório')
})

export const validateEmail = async (email: string): Promise<boolean> => {
  try {
    await emailSchema.validate({ email })
    return true
  } catch {
    return false
  }
}

export const getDoctorInitials = (name: string) => {
  const parts = name
    .replace(/^(Dr\.|Dra\.)\s*/i, '')
    .trim()
    .split(' ')
  if (parts.length === 0 || !parts[0]) return ''
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Title case do nome completo, preservando partículas em minúsculo (ex: "de", "dos").
export const capitalizeFullName = (name: string): string => {
  const particles = ['de', 'da', 'do', 'das', 'dos', 'e']
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index) => {
      const lower = word.toLowerCase()
      if (index > 0 && particles.includes(lower)) return lower
      return lower.charAt(0).toUpperCase() + lower.slice(1)
    })
    .join(' ')
}

// Nome curto do DoctorInfo — mesma regra de `getFirstAndSecondName`.
export const capitalizeDoctorName = (name: string) => getFirstAndSecondName(name)

// "Dr. Bárbara Maia" — prefixo "Dr." fixo é decisão pendente de produto (não
// flexiona Dra.). Como `getFirstAndSecondName` já descarta um "Dr."/"Dra." vindo
// no nome, o resultado tem sempre um único "Dr." ("Dr. Wagner Tavares"). Difere
// de `formatDoctorName` abaixo, que usa "Dr(a)." e o nome completo.
export const formatDoctorShortName = (name: string): string => {
  const shortName = getFirstAndSecondName(name)
  return shortName ? `Dr. ${shortName}` : ''
}

export const formatDoctorName = (name: string) => {
  const pascalCased = name
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
  return `Dr(a). ${pascalCased}`
}

// Corta o texto em `maxLength` caracteres de CONTEÚDO e acrescenta a elipse — ela
// não conta no limite. Usar quando o corte por CSS (`truncate`) não basta porque a
// largura da coluna é elástica; sempre acompanhado de tooltip com o texto completo.
export const truncateText = (value: string, maxLength: number): string => {
  if (!value || value.length <= maxLength) return value
  return `${value.slice(0, maxLength)}…`
}

export function parseBoolToString(data?: boolean): string | undefined {
  if (data === undefined || data === null) return undefined
  if (data) return 'true'
  return 'false'
}

export function parseStringToBool(data?: string): boolean | undefined {
  if (data === undefined) return undefined
  return data === 'true'
}
